import { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Trophy, Users, 
  Activity, BarChart3, Target, ChevronRight, DatabaseZap
} from 'lucide-react';
import { Baseball } from '../../../components/icons/SportsIcons';

// Import services and hooks
import BaseballService from '../../../components/dashboards/services/baseballService';
import { useGames, useStandings, useLeagues, useSeasons, useBaseballCache } from '../../../components/dashboards/hooks/useBaseballData';
import { useQuickPrediction } from '../../../components/dashboards/hooks/useQuickPrediction';
import { auth } from '../../../firebase';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { baseballMobilePtBR } from '../../../components/mappings/sports-dash-mappings/baseball-mobile.pt-br';
import { useNavigate } from 'react-router-dom';

// Import components
import ErrorBoundary from '../../../components/shared/error/ErrorBoundary';
import MobileStandingsTable from './MobileStandingsTable';
import { LoadingSpinner } from '../../../components/shared/loading/LoadingSpinner';
import { EmptyState } from '../../../components/shared/loading/EmptyState';

import MobileGameCardWithPrediction from './MobileGameCardWithPrediction';
import MobileBaseballPredictionBar from './MobileBaseballPredictionBar';
import BottomSheet from '../../../components/dashboards/components/shared/BottomSheet';
import PullToRefresh from '../../../components/shared/PullToRefresh';
import QuickPickUpgradeNotice from '../../../components/QuickPickUpgradeNotice';
import StarCoinsModal from '../../../components/starCoinSystem/StarCoinsModal';

// Import new Rail Navigation components
import RailNavigation from '../../../components/dashboards/mobile/RailNavigation';
import { DatePickerSheet, LeagueSheet, SeasonSheet } from '../../../components/dashboards/mobile/FilterSheets';
import useBaseballRailFilters from '../../../components/dashboards/mobile/hooks/useBaseballRailFilters';

const BaseballDashMobile = () => {
  const navigate = useNavigate();
  
  // Internationalization
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (baseballMobilePtBR[text] || text) : text;

  const MOBILE_TABS = [
    { id: 'games', label: t('Games'), icon: Activity, color: 'blue' },
    { id: 'standings', label: t('Standings'), icon: BarChart3, color: 'blue' },
    { id: 'players', label: t('Players'), icon: Target, color: 'violet' },
  ];

  // State management
  const [activeTab, setActiveTab] = useState('games');
  const [showStarCoinsModal, setShowStarCoinsModal] = useState(false);
  
  // Bottom sheet states
  const [showOddsSheet, setShowOddsSheet] = useState(false);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);

  // Games state
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // Mobile: Rail filters handle league selection
  const [selectedLeague, setSelectedLeague] = useState(null);
  // Default to 2025 season (seasons are 4-digit years)
  const [selectedSeason, setSelectedSeason] = useState(2025);
  const [selectedDivision, setSelectedDivision] = useState('all'); // Add division state
  const [gamesPage, setGamesPage] = useState(1);

  // Standings state - use same league/season
  const standingsLeague = selectedLeague;
  const standingsSeason = selectedSeason;

  // Players state - not implemented yet for baseball API
  // const playersLeague = selectedLeague;
  // const playersSeason = selectedSeason;
  // const [playersPage, setPlayersPage] = useState(1);

  // Data hooks
  const { leagues, loading: leaguesLoading } = useLeagues();
  const { seasons } = useSeasons();
  useBaseballCache(); // Just initialize the cache

  // Mobile uses rail filters - don't auto-select here
  // The rail component handles league selection

  // Prediction hook with caching support
  const {
    isLoading: isPredicting,
    usageState,
    error: predictionError,
    showUpgradeNotice,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    setShowUpgradeNotice,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    // NEW: Caching features
    frozenSelections,
    activeFilters,
    preserveFilterState,
    checkCachedPredictions,
    isGameFrozen,
    refundNotice
  } = useQuickPrediction();

  // No longer need isInitialMount - we'll sync immediately per integration guide
  
  // Convert Date to string for rail filters
  const initialDateStr = useMemo(() => {
    const date = selectedDate instanceof Date ? selectedDate : new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [selectedDate]);

  // NEW: Rail Navigation Hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters,
    primaryTabs,
    filterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter,
    resetFilters,
    showDateSheet,
    setShowDateSheet,
    showLeagueSheet,
    setShowLeagueSheet,
    showSeasonSheet,
    setShowSeasonSheet
  } = useBaseballRailFilters({
    initialDate: initialDateStr,
    initialLeague: selectedLeague,
    initialSeason: selectedSeason,
    onFiltersChange: (newFilters) => {
      // Always sync state when rail filters change - no skipping!
      // Convert date string to Date object for component state
      if (newFilters.date) {
        let newDate;
        if (typeof newFilters.date === 'string') {
          const [year, month, day] = newFilters.date.split('-').map(Number);
          newDate = new Date(year, month - 1, day);
        } else if (newFilters.date instanceof Date) {
          newDate = newFilters.date;
        } else {
          newDate = new Date();
        }
        setSelectedDate(newDate);
      }
      
      // Sync league - use null if not set
      const normalizedLeague = newFilters.league || null;
      setSelectedLeague(normalizedLeague);
      
      // Sync season
      const normalizedSeason = newFilters.season || new Date().getFullYear();
      setSelectedSeason(normalizedSeason);
      
      // Sync division
      const normalizedDivision = newFilters.division || 'all';
      setSelectedDivision(normalizedDivision);
    }
  });

  // Data fetching hooks
  const gamesQuery = useGames({
    date: selectedDate,
    league: selectedLeague?.id,
    season: selectedSeason,
    enabled: activeTab === 'games'
  });

  const standingsQuery = useStandings({
    league: standingsLeague?.id,
    season: standingsSeason,
    enabled: activeTab === 'standings' && !!standingsLeague
  });

  // For now, we'll use empty players data since baseball API doesn't have player stats endpoint
  // const playersQuery = {
  //   data: [],
  //   loading: false,
  //   error: null
  // };

  // Pagination settings
  const gamesPerPage = 10;
  // const playersPerPage = 20; // Not used yet

  // Helper to determine which division a team belongs to
  const getTeamDivision = (teamName) => {
    if (!teamName) return null;
    
    // AL East teams
    const alEast = ['Orioles', 'Red Sox', 'Yankees', 'Blue Jays', 'Rays'];
    // AL Central teams
    const alCentral = ['Guardians', 'Tigers', 'Royals', 'Twins', 'White Sox'];
    // AL West teams
    const alWest = ['Astros', 'Rangers', 'Mariners', 'Athletics', 'Angels'];
    // NL East teams
    const nlEast = ['Braves', 'Phillies', 'Mets', 'Marlins', 'Nationals'];
    // NL Central teams
    const nlCentral = ['Brewers', 'Cubs', 'Cardinals', 'Pirates', 'Reds'];
    // NL West teams
    const nlWest = ['Dodgers', 'Diamondbacks', 'Padres', 'Giants', 'Rockies'];
    
    if (alEast.some(team => teamName.includes(team))) return 'al-east';
    if (alCentral.some(team => teamName.includes(team))) return 'al-central';
    if (alWest.some(team => teamName.includes(team))) return 'al-west';
    if (nlEast.some(team => teamName.includes(team))) return 'nl-east';
    if (nlCentral.some(team => teamName.includes(team))) return 'nl-central';
    if (nlWest.some(team => teamName.includes(team))) return 'nl-west';
    
    // Check for league only
    const alTeams = [...alEast, ...alCentral, ...alWest];
    const nlTeams = [...nlEast, ...nlCentral, ...nlWest];
    if (alTeams.some(team => teamName.includes(team))) return 'al';
    if (nlTeams.some(team => teamName.includes(team))) return 'nl';
    
    return null;
  };
  
  // Filter and sort games by division and status
  const sortedGames = useMemo(() => {
    let games = gamesQuery.games || [];
    if (games.length === 0) return [];
    
    // Filter by division if not 'all'
    if (selectedDivision !== 'all') {
      games = games.filter(game => {
        const homeTeam = game.teams?.home?.name;
        const awayTeam = game.teams?.away?.name;
        const homeDivision = getTeamDivision(homeTeam);
        const awayDivision = getTeamDivision(awayTeam);
        
        // For league filters (al, nl), check if either team is in that league
        if (selectedDivision === 'al') {
          return homeDivision?.startsWith('al') || awayDivision?.startsWith('al');
        }
        if (selectedDivision === 'nl') {
          return homeDivision?.startsWith('nl') || awayDivision?.startsWith('nl');
        }
        
        // For specific divisions, check if either team is in that division
        return homeDivision === selectedDivision || awayDivision === selectedDivision;
      });
    }
    
    return [...games].sort((a, b) => {
      const statusA = a.status?.short;
      const statusB = b.status?.short;
      
      // Check if games are live (baseball uses IN1-IN9 for innings)
      const aIsLive = statusA && (
        statusA.startsWith('IN') || 
        statusA === 'LIVE' || 
        statusA === 'IN_PLAY' ||
        ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(statusA)
      );
      const bIsLive = statusB && (
        statusB.startsWith('IN') || 
        statusB === 'LIVE' || 
        statusB === 'IN_PLAY' ||
        ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(statusB)
      );
      
      // Check if games are finished
      const aIsFinished = statusA && (statusA === 'FT' || statusA === 'AOT' || statusA === 'FINISHED');
      const bIsFinished = statusB && (statusB === 'FT' || statusB === 'AOT' || statusB === 'FINISHED');
      
      // Priority order: Live > Upcoming (NS) > Finished
      if (aIsLive && !bIsLive) return -1;
      if (!aIsLive && bIsLive) return 1;
      
      // Both not live - check if one is finished
      if (!aIsLive && !bIsLive) {
        if (!aIsFinished && bIsFinished) return -1;
        if (aIsFinished && !bIsFinished) return 1;
      }
      
      // Same status category - sort by time for upcoming games
      if (!aIsLive && !aIsFinished && !bIsLive && !bIsFinished) {
        return new Date(a.date) - new Date(b.date);
      }
      
      return 0;
    });
  }, [gamesQuery.games]);

  // Check for cached predictions when games change
  useEffect(() => {
    const checkCached = async () => {
      if (sortedGames.length > 0 && activeTab === 'games') {
        const gameIds = sortedGames.map(game => game.id).filter(Boolean);
        if (gameIds.length > 0) {
          const cachedPreds = await checkCachedPredictions('baseball', gameIds);
          if (Object.keys(cachedPreds).length > 0) {
            console.log(`[BaseballDashMobile] Loaded ${Object.keys(cachedPreds).length} cached predictions`);
          }
        }
      }
    };
    checkCached();
  }, [sortedGames, activeTab, checkCachedPredictions]);

  // Calculate paginated data
  const paginatedGames = useMemo(() => {
    const start = (gamesPage - 1) * gamesPerPage;
    return sortedGames.slice(start, start + gamesPerPage);
  }, [sortedGames, gamesPage, gamesPerPage]);

  // Players pagination not used yet (no player data from API)
  // const paginatedPlayers = useMemo(() => {
  //   const start = (playersPage - 1) * playersPerPage;
  //   return playersQuery.data.slice(start, start + playersPerPage);
  // }, [playersQuery.data, playersPage, playersPerPage]);

  const totalGamesPages = Math.ceil((gamesQuery.games || []).length / gamesPerPage);
  // const totalPlayersPages = Math.ceil(playersQuery.data.length / playersPerPage);

  // Reset pagination when filters change
  useEffect(() => {
    setGamesPage(1);
  }, [selectedDate, selectedLeague, selectedSeason, selectedDivision]);

  // useEffect(() => {
  //   setPlayersPage(1);
  // }, [playersLeague, playersSeason]);

  // Listen for star coins modal event
  useEffect(() => {
    const handleOpenStarCoinsModal = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowStarCoinsModal(true);
    };
    
    window.addEventListener('openStarCoinsModal', handleOpenStarCoinsModal);
    return () => {
      window.removeEventListener('openStarCoinsModal', handleOpenStarCoinsModal);
    };
  }, []);

  // Handlers
  const handleRefresh = useCallback(() => {
    switch (activeTab) {
      case 'games':
        gamesQuery.refetch && gamesQuery.refetch();
        break;
      case 'standings':
        standingsQuery.refetch && standingsQuery.refetch();
        break;
      case 'players':
        // No refetch for players yet
        break;
    }
  }, [activeTab, gamesQuery, standingsQuery]);

  const handleViewOdds = useCallback(async (game) => {
    setSelectedGameForOdds(game);
    setShowOddsSheet(true);
    
    // Try to fetch odds for this game
    try {
      const oddsData = await BaseballService.getOdds(null, null, game.id);
      if (oddsData.response && oddsData.response.length > 0 && oddsData.response[0].bookmakers) {
        const bookmakers = oddsData.response[0].bookmakers;
        setSelectedGameForOdds({ ...game, oddsData: bookmakers });
      }
    } catch (error) {
      console.error('Failed to fetch odds:', error);
    }
  }, []);

  const leagueOptions = useMemo(() =>
    leagues.map(league => ({
      // API returns flat structure: {id, name, logo, country, seasons}
      id: league.id,
      name: league.name,
      logo: league.logo
    })), [leagues]
  );

  const seasonOptions = useMemo(() => 
    seasons.map(season => ({
      id: season,
      name: season.toString()
    })), [seasons]
  );

  return (
    <ErrorBoundary onReset={handleRefresh}>
      <div className="min-h-screen bg-gradient-to-br from-[#050311] via-[#0c1a2d] to-[#081b25]">
        {/* Spacer to align with unified mobile nav */}
        <div className="h-[60px]" />

        {/* Rail Navigation System */}
        <div className="sticky top-[60px] z-40 bg-[#050311]/95 backdrop-blur-lg border-b border-white/10" style={{ position: '-webkit-sticky', top: '60px' }}>
          <RailNavigation
            sport="baseball"
            primaryTabs={primaryTabs}
            filters={filterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>

        {/* Pull to Refresh Container */}
        <PullToRefresh onRefresh={handleRefresh}>
          <div className="px-4 py-4" style={{ paddingBottom: 'calc(128px + env(safe-area-inset-bottom))' }}>
            {/* Content based on active tab */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Games Tab */}
                {activeTab === 'games' && (
                  <div className="space-y-3">
                    {gamesQuery.loading ? (
                      <LoadingSpinner message={t('Loading games...')} />
                    ) : gamesQuery.error ? (
                      <EmptyState 
                        title={t('Failed to load games')}
                        description={gamesQuery.error}
                        action={
                          <button
                            onClick={gamesQuery.refetch}
                            className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded-lg"
                          >
                            Try Again
                          </button>
                        }
                      />
                    ) : !gamesQuery.games || gamesQuery.games.length === 0 ? (
                      <EmptyState 
                        title={t('No games found')}
                        description={t('Try selecting a different date')}
                        icon={Calendar}
                      />
                    ) : (
                      <>
                        <div className="space-y-3">
                          {paginatedGames.map(game => (
                            <MobileGameCardWithPrediction
                              key={game.id}
                              game={game}
                              onViewOdds={handleViewOdds}
                              isSelected={isGameSelected(game)}
                              isFrozen={isGameFrozen(game.id)}
                              onToggleSelection={toggleGameSelection}
                              prediction={getGamePrediction(game.id)}
                              showPredictionButton={true}
                            />
                          ))}
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalGamesPages > 1 && (
                          <div className="mt-6 flex items-center justify-center gap-4">
                            <button
                              onClick={() => setGamesPage(p => Math.max(1, p - 1))}
                              disabled={gamesPage === 1}
                              className="px-4 py-2 bg-white/10 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="text-white/60 text-sm font-medium">
                              {gamesPage} / {totalGamesPages}
                            </span>
                            <button
                              onClick={() => setGamesPage(p => Math.min(totalGamesPages, p + 1))}
                              disabled={gamesPage === totalGamesPages}
                              className="px-4 py-2 bg-white/10 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Standings Tab */}
                {activeTab === 'standings' && (
                  <div className="w-full">
                    {!standingsLeague ? (
                      <EmptyState 
                        title={t('Select a league')}
                        description={t('Choose a league from filters to view standings')}
                        icon={Trophy}
                      />
                    ) : (
                      <div className="overflow-x-auto -mx-4">
                        <div className="px-4">
                          <MobileStandingsTable
                            standings={standingsQuery.standings}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Players Tab */}
                {activeTab === 'players' && (
                  <div className="w-full">
                    <EmptyState 
                      title={t('Player stats coming soon')}
                      description={t('MLB player statistics will be available in a future update')}
                      icon={Users}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </PullToRefresh>

        {/* Bottom Navigation - Enhanced Touch Targets */}
        <div className="fixed bottom-0 left-0 right-0 bg-[#050311]/95 backdrop-blur-lg border-t border-white/10 z-50">
          <div className="flex items-center justify-around" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {MOBILE_TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const Icon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex flex-col items-center justify-center flex-1 min-h-[56px] py-3 rounded-md transition-all ${
                    isActive ? 'text-blue-400' : 'text-white/60'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent', minHeight: '56px' }}
                >
                  <Icon size={22} className="mb-1" />
                  <span className="text-xs font-medium">{tab.label}</span>
                  {isActive && (
                    <motion.div 
                      layoutId="activeTab"
                      className="absolute bottom-0 left-4 right-4 h-0.5 bg-blue-400 rounded-full"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* NEW: Filter Sheets */}
        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={typeof railFilters.date === 'string'
            ? (() => {
                // Parse YYYY-MM-DD string as local date (not UTC)
                const [year, month, day] = railFilters.date.split('-').map(Number);
                return new Date(year, month - 1, day);
              })()
            : railFilters.date
          }
          onDateSelect={(date) => {
            // DatePickerSheet returns a Date object, convert to string for rail filters
            let dateStr;
            if (date instanceof Date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              dateStr = `${year}-${month}-${day}`;
            } else {
              dateStr = date;
            }
            
            updateFilter('date', dateStr);
            // Update the Date object for existing state
            setSelectedDate(date instanceof Date ? date : new Date(date));
          }}
        />
        
        <LeagueSheet
          isOpen={showLeagueSheet}
          onClose={() => setShowLeagueSheet(false)}
          selectedLeagues={railFilters.league?.id || railFilters.league}
          onLeagueSelect={(league) => {
            // Find the full league object from leagueOptions
            const fullLeague = leagueOptions.find(l => l.id === league) || { id: league, name: league };
            // Only update through rail filters - onFiltersChange will sync selectedLeague
            updateFilter('league', fullLeague);
            // DON'T setSelectedLeague here - let onFiltersChange handle it!
          }}
          leagues={leagueOptions}
          sport="baseball"
        />
        
        <SeasonSheet
          isOpen={showSeasonSheet}
          onClose={() => setShowSeasonSheet(false)}
          currentSeason={railFilters.season}
          onSeasonChange={(season) => {
            // Only update through rail filters - onFiltersChange will sync selectedSeason
            updateFilter('season', season);
            // DON'T setSelectedSeason here - let onFiltersChange handle it!
          }}
          availableSeasons={seasonOptions.map(s => s.id)}
          sport="baseball"
        />

        {/* Odds Bottom Sheet */}
        <BottomSheet
          isOpen={showOddsSheet}
          onClose={() => {
            setShowOddsSheet(false);
            setSelectedGameForOdds(null);
          }}
          title="Betting Odds"
        >
          {selectedGameForOdds && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <img 
                    src={selectedGameForOdds.teams?.home?.logo} 
                    alt="" 
                    className="w-8 h-8"
                  />
                  <span className="font-medium text-white">
                    {selectedGameForOdds.teams?.home?.name}
                  </span>
                </div>
                <span className="text-white/60">vs</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white">
                    {selectedGameForOdds.teams?.away?.name}
                  </span>
                  <img 
                    src={selectedGameForOdds.teams?.away?.logo} 
                    alt="" 
                    className="w-8 h-8"
                  />
                </div>
              </div>

              {!selectedGameForOdds.oddsData ? (
                <EmptyState 
                  title="No odds available"
                  description="Odds data not available for this game"
                />
              ) : (
                <div className="space-y-4">
                  {selectedGameForOdds.oddsData.map((bookmaker, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-4">
                      <h4 className="font-medium text-white mb-3">
                        {bookmaker.name}
                      </h4>
                      {bookmaker.bets.map((bet, betIdx) => (
                        <div key={betIdx} className="space-y-2">
                          <p className="text-sm text-white/60">{bet.name}</p>
                          <div className="grid grid-cols-3 gap-2">
                            {bet.values.map((value, valueIdx) => (
                              <button
                                key={valueIdx}
                                className="p-3 bg-white/10 rounded-lg text-center hover:bg-white/20 transition-colors"
                              >
                                <div className="text-xs text-white/60">
                                  {value.value}
                                </div>
                                <div className="text-lg font-bold text-blue-400">
                                  {value.odd}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </BottomSheet>

        {/* Mobile Prediction Bar */}
        <MobileBaseballPredictionBar
          selectionCount={selectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          error={predictionError}
          refundNotice={refundNotice}
          onPredict={() => {
            // Preserve current dashboard state
            preserveFilterState({
              date: selectedDate,
              league: selectedLeague,
              season: selectedSeason,
              division: selectedDivision,
              tab: activeTab
            });
            // Then trigger prediction
            predictSelectedGames('baseball');
          }}
          onClear={clearSelections}
        />

        {/* Upgrade Notice */}
        {showUpgradeNotice && (
          <QuickPickUpgradeNotice
            onClose={() => setShowUpgradeNotice(false)}
            isMobile={true}
            sport="MLB"
          />
        )}

        {/* Star Coins Modal */}
        {showStarCoinsModal && (
          <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.3, type: "spring", damping: 25 }}
              className="fixed inset-x-4 top-[50%] -translate-y-[50%] max-h-[90vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl border border-white/10"
            >
              <StarCoinsModal
                isOpen={true}
                onClose={() => setShowStarCoinsModal(false)}
                userId={auth.currentUser?.uid}
              />
            </motion.div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default BaseballDashMobile;